import { shopifyProvinces } from "@redotech/locale/provinces";
import {
  getCountryNameFromCode,
  RedoShopifyCountryCode,
} from "@redotech/redo-inventory-common/fulfillment-country-code";
import type { FulfillmentOrderAddress } from "@redotech/redo-model/fulfillments/fulfillment-order-address";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

interface EditAddressModalProps {
  isOpen: boolean;
  title: string;
  address: FulfillmentOrderAddress;
  onSave(address: FulfillmentOrderAddress): void;
  onClose(): void;
}

const countryOptions: RedoListItem<RedoShopifyCountryCode>[] = Object.values(
  RedoShopifyCountryCode,
).map((code) => ({
  id: code,
  type: "text" as const,
  text: getCountryNameFromCode(code),
  value: code,
}));

export const EditAddressModal = memo(function EditAddressModal({
  isOpen,
  title,
  address,
  onSave,
  onClose,
}: EditAddressModalProps) {
  const [draft, setDraft] = useState<FulfillmentOrderAddress>(address);

  useEffect(() => {
    if (isOpen) {
      setDraft(address);
    }
  }, [isOpen, address]);

  const update = useCallback((fields: Partial<FulfillmentOrderAddress>) => {
    setDraft((prev) => ({ ...prev, ...fields }));
  }, []);

  const handleSave = useCallback(() => {
    onSave(draft);
    onClose();
  }, [draft, onSave, onClose]);

  const selectedCountryCode = useMemo(() => {
    return (
      (draft.countryCode as RedoShopifyCountryCode) ?? RedoShopifyCountryCode.Us
    );
  }, [draft.countryCode]);

  const availableProvinces = useMemo(() => {
    return shopifyProvinces.filter(
      (p) => p.countryCode === selectedCountryCode,
    );
  }, [selectedCountryCode]);

  const provinceOptions: RedoListItem<string | null>[] = useMemo(() => {
    return [
      { id: "none", type: "text" as const, text: "None", value: null },
      ...availableProvinces.map(({ province, provinceCode }) => ({
        id: provinceCode,
        type: "text" as const,
        text: province,
        value: provinceCode,
      })),
    ];
  }, [availableProvinces]);

  const selectedCountryItem = useMemo(
    () => ({
      id: selectedCountryCode,
      type: "text" as const,
      text: getCountryNameFromCode(selectedCountryCode),
      value: selectedCountryCode,
    }),
    [selectedCountryCode],
  );

  const selectedProvinceItem = useMemo(() => {
    if (draft.province) {
      const match = availableProvinces.find(
        (p) => p.provinceCode === draft.province,
      );
      return {
        id: draft.province,
        type: "text" as const,
        text: match?.province ?? draft.province,
        value: draft.province,
      };
    }
    return { id: "none", type: "text" as const, text: "None", value: null };
  }, [draft.province, availableProvinces]);

  return (
    <RedoModal
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{ text: "Save", onClick: handleSave }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      title={title}
    >
      <Flex dir="column" gap="md" p="xl">
        <RedoTextInput
          label="Name"
          setValue={(v) => update({ name: v })}
          size="sm"
          value={draft.name ?? ""}
        />
        <RedoTextInput
          label="Company"
          setValue={(v) => update({ company: v })}
          size="sm"
          value={draft.company ?? ""}
        />
        <RedoSingleSelectDropdownInput<RedoShopifyCountryCode>
          label="Country"
          options={countryOptions}
          optionSelected={({ item }) => {
            update({ countryCode: item.value, province: undefined });
          }}
          selectedItem={selectedCountryItem}
          size="xs"
        />
        <RedoTextInput
          label="Address line 1"
          setValue={(v) => update({ address1: v })}
          size="sm"
          value={draft.address1 ?? ""}
        />
        <RedoTextInput
          label="Address line 2"
          setValue={(v) => update({ address2: v })}
          size="sm"
          value={draft.address2 ?? ""}
        />
        <Flex gap="md">
          <Flex grow={1}>
            <RedoTextInput
              fullWidth
              label="City"
              setValue={(v) => update({ city: v })}
              size="sm"
              value={draft.city ?? ""}
            />
          </Flex>
          <Flex grow={1}>
            {availableProvinces.length > 0 ? (
              <RedoSingleSelectDropdownInput<string | null>
                label={
                  selectedCountryCode === RedoShopifyCountryCode.Us
                    ? "State"
                    : "Province"
                }
                options={provinceOptions}
                optionSelected={({ item }) => {
                  update({ province: item.value ?? undefined });
                }}
                selectedItem={selectedProvinceItem}
                size="xs"
              />
            ) : (
              <RedoTextInput
                fullWidth
                label="State / Province"
                setValue={(v) => update({ province: v })}
                size="sm"
                value={draft.province ?? ""}
              />
            )}
          </Flex>
        </Flex>
        <RedoTextInput
          label="Postal code"
          setValue={(v) => update({ zip: v })}
          size="sm"
          value={draft.zip ?? ""}
        />
        <Flex gap="md">
          <Flex grow={1}>
            <RedoTextInput
              fullWidth
              label="Phone"
              setValue={(v) => update({ phone: v })}
              size="sm"
              value={draft.phone ?? ""}
            />
          </Flex>
          <Flex grow={1}>
            <RedoTextInput
              fullWidth
              label="Email"
              setValue={(v) => update({ email: v })}
              size="sm"
              value={draft.email ?? ""}
            />
          </Flex>
        </Flex>
      </Flex>
    </RedoModal>
  );
});
